import { useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useToast } from "@/hooks/use-toast";
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalFooter } from "@/components/ui/modal";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Paperclip, Send, Loader2, X } from "lucide-react";
import { submitFeedback } from "@/lib/api";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const categories = [
  { value: "bug", label: "Bug" },
  { value: "suggestion", label: "Suggestion" },
  { value: "praise", label: "Praise" },
  { value: "other", label: "Other" },
] as const;

type CategoryValue = (typeof categories)[number]["value"];

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const { toast } = useToast();
  const { user } = useUser();
  const [category, setCategory] = useState<CategoryValue>("bug");
  const [description, setDescription] = useState("");
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = description.trim().length > 0 && !isSubmitting;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (file && file.size > MAX_FILE_SIZE) {
      toast({
        title: "File too large",
        description: "Maximum file size is 2MB. Please choose a smaller image.",
        variant: "destructive",
      });
      event.target.value = "";
      setScreenshotFile(null);
      return;
    }
    setScreenshotFile(file);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);

    try {
      await submitFeedback({
        category,
        description: description.trim(),
        screenshot: screenshotFile || undefined,
        userEmail: user?.primaryEmailAddress?.emailAddress || undefined,
      });

      toast({
        title: "Thank you!",
        description: "Your feedback has been received.",
      });

      // Reset form and close modal
      setCategory("bug");
      setDescription("");
      setScreenshotFile(null);
      onOpenChange(false);

    } catch (error: any) {
      console.error('Failed to submit feedback:', error);
      toast({
        title: "Submission failed",
        description: error?.message || "Could not submit your feedback. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader className="relative">
          <ModalTitle>Report an Issue or Share Feedback</ModalTitle>
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4 h-6 w-6 p-0"
            onClick={() => onOpenChange(false)}
            aria-label="Close feedback modal"
          >
            <X className="h-4 w-4" />
          </Button>
          <ModalDescription>
            Your feedback helps us polish the HiPo AI Coach experience. When describing the problem you faced please ensure you describe the problem to ensure we are able to address it effectively (text mandatory for sending feedback).
          </ModalDescription>
        </ModalHeader>
        <form className="px-6 pb-6 space-y-6" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="category">Category</Label>
            <div className="relative">
              <select
                id="category"
                value={category}
                onChange={(event) => setCategory(event.target.value as CategoryValue)}
                aria-label="Issue category"
                className="w-full appearance-none rounded-md border border-border bg-white/80 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {categories.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground">
                ▾
              </span>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Feedback</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Tell us what's working, what's not, and what you'd love to see next."
              rows={6}
              className="resize-none bg-white/80"
              required
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="screenshot">Screenshot (optional, max 2MB)</Label>
            <div className="flex items-center gap-3 rounded-lg border border-dashed border-border/60 bg-white/70 px-4 py-3">
              <Paperclip className="h-4 w-4 text-muted-foreground" aria-hidden />
              <Input
                id="screenshot"
                type="file"
                accept="image/*"
                className="cursor-pointer border-0 bg-transparent p-0 file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary-foreground hover:file:bg-primary/90"
                onChange={handleFileChange}
              />
            </div>
            {screenshotFile && (
              <p className="text-xs text-emerald-600">
                ✓ {screenshotFile.name} ({(screenshotFile.size / 1024).toFixed(0)} KB)
              </p>
            )}
          </div>

          <ModalFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
